import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { GlassButton } from '../ui/GlassButton';
import { Plus, FileText } from 'lucide-react';
import ProductsList from './ProductsList';
import ProductOrders from './ProductOrders';
import ProductCustomers from './ProductCustomers';
import ProductDiscounts from './ProductDiscounts';
import PaymentSettings from './PaymentSettings';
import VideoSettings from './VideoSettings';
import WhitepaperGenerator from './WhitepaperGenerator';
import { WhitepaperGenerateResponse } from '../../services/api';
import './Products.css';

type Tab = 'products' | 'orders' | 'customers' | 'discounts' | 'settings' | 'video';

const TABS: { key: Tab; label: string }[] = [
  { key: 'products', label: 'Products' },
  { key: 'orders', label: 'Orders' },
  { key: 'customers', label: 'Customers' },
  { key: 'discounts', label: 'Discounts' },
  { key: 'settings', label: 'Settings' },
  { key: 'video', label: 'Video Settings' },
];

const ProductsModule: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState<Tab>('products');

  // Whitepaper generator modal — opened either from the header action or via
  // a deep link from ContentCatalogue's per-article "Whitepaper" action
  // (/products?whitepaper_from=<task_id>&whitepaper_project=<project_id>).
  const [whitepaperOpen, setWhitepaperOpen] = useState(false);
  const [whitepaperInitialTaskId, setWhitepaperInitialTaskId] = useState<string | null>(null);
  const [whitepaperInitialProjectId, setWhitepaperInitialProjectId] = useState<string | null>(null);
  const [productsRefreshKey, setProductsRefreshKey] = useState(0);

  useEffect(() => {
    const fromTask = searchParams.get('whitepaper_from');
    if (fromTask) {
      setWhitepaperInitialTaskId(fromTask);
      setWhitepaperInitialProjectId(searchParams.get('whitepaper_project'));
      setWhitepaperOpen(true);
      setActiveTab('products');
      // Clear the deep-link params so a refresh/re-open doesn't re-trigger it
      const next = new URLSearchParams(searchParams);
      next.delete('whitepaper_from');
      next.delete('whitepaper_project');
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openWhitepaperGenerator = () => {
    setWhitepaperInitialTaskId(null);
    setWhitepaperInitialProjectId(null);
    setWhitepaperOpen(true);
  };

  const handleWhitepaperGenerated = (product: WhitepaperGenerateResponse) => {
    setProductsRefreshKey(k => k + 1);
    navigate(`/products/${product.product_id}`);
  };

  return (
    <div className="products-module">
      <div className="products-header">
        <h1>My Products</h1>
        {activeTab === 'products' && (
          <div className="products-header-actions">
            <GlassButton
              variant="secondary"
              icon={<FileText size={16} />}
              onClick={openWhitepaperGenerator}
            >
              New Whitepaper
            </GlassButton>
            <GlassButton
              variant="primary"
              icon={<Plus size={16} />}
              onClick={() => navigate('/products/new')}
            >
              New Product
            </GlassButton>
          </div>
        )}
      </div>

      <div className="products-tab-bar">
        {TABS.map(tab => (
          <button
            key={tab.key}
            className={`products-tab${activeTab === tab.key ? ' active' : ''}`}
            onClick={() => setActiveTab(tab.key)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'products' && (
        <ProductsList refreshKey={productsRefreshKey} onNewWhitepaper={openWhitepaperGenerator} />
      )}
      {activeTab === 'orders' && <ProductOrders />}
      {activeTab === 'customers' && <ProductCustomers />}
      {activeTab === 'discounts' && <ProductDiscounts />}
      {activeTab === 'settings' && <PaymentSettings />}
      {activeTab === 'video' && <VideoSettings />}

      <WhitepaperGenerator
        isOpen={whitepaperOpen}
        onClose={() => setWhitepaperOpen(false)}
        initialTaskId={whitepaperInitialTaskId}
        initialProjectId={whitepaperInitialProjectId}
        onGenerated={handleWhitepaperGenerated}
      />
    </div>
  );
};

export default ProductsModule;
